import moment from "moment";

import { CROP_TYPE_FCPO, FieldListField, FieldPracticeResponse } from "models";

import { isDateValidConsideringLastConfirmedCrop } from "pages/app/all-field-view/utils/CropValidationUtils";

export const CROP_TYPE_OPTIONS = [CROP_TYPE_FCPO.MAIN, CROP_TYPE_FCPO.COVER, CROP_TYPE_FCPO.UNDERSOWING];

export const CROP_TYPE_TRANSLATION_KEY: Record<CROP_TYPE_FCPO, string> = {
  [CROP_TYPE_FCPO.MAIN]: "mainCrop",
  [CROP_TYPE_FCPO.COVER]: "coverCrop",
  [CROP_TYPE_FCPO.UNDERSOWING]: "undersowing",
};

// Undersowing needs a standing Main crop; Cover crop doesn't
export const CROP_TYPES_REQUIRING_MAIN_CROP = [CROP_TYPE_FCPO.UNDERSOWING];

// mirrors Timeline's placeholder/save-time rule for these two crop types
export const CROP_TYPES_LOCKED_AFTER_VERIFICATION = [CROP_TYPE_FCPO.COVER, CROP_TYPE_FCPO.UNDERSOWING];

const fieldHasCropType = (fieldPractices: Record<string, FieldPracticeResponse>, fieldId: string, year: number, cropType: CROP_TYPE_FCPO) =>
  (fieldPractices[fieldId]?.crops ?? []).some((crop) => crop.cropType === cropType && crop.year === year);

const isCropTypeYearLocked = (fieldPractices: Record<string, FieldPracticeResponse>, fieldId: string, year: number, cropType: CROP_TYPE_FCPO) => {
  if (!CROP_TYPES_LOCKED_AFTER_VERIFICATION.includes(cropType)) return false;
  const crops = fieldPractices[fieldId]?.crops;
  const yearStart = moment({ year }).startOf("year");
  const yearEnd = moment({ year }).endOf("year");
  return !isDateValidConsideringLastConfirmedCrop(yearStart, yearEnd, crops, cropType, undefined);
};

export const getFieldsWithoutCropType = ({
  fields,
  fieldPractices,
  year,
  cropType,
}: {
  fields: FieldListField[];
  fieldPractices: Record<string, FieldPracticeResponse>;
  year: number;
  cropType: CROP_TYPE_FCPO;
}): FieldListField[] =>
  fields.filter((field) => {
    if (CROP_TYPES_REQUIRING_MAIN_CROP.includes(cropType) && !fieldHasCropType(fieldPractices, field.id, year, CROP_TYPE_FCPO.MAIN)) return false;
    if (isCropTypeYearLocked(fieldPractices, field.id, year, cropType)) return false;
    return true;
  });

export const getFieldsMissingMainCrop = ({
  fields,
  fieldPractices,
  year,
}: {
  fields: FieldListField[];
  fieldPractices: Record<string, FieldPracticeResponse>;
  year: number;
}): FieldListField[] => fields.filter((field) => !fieldHasCropType(fieldPractices, field.id, year, CROP_TYPE_FCPO.MAIN));

// true when every field has at least one crop of any of the given types that year
export const allFieldsHaveAnyCropType = (
  fields: FieldListField[],
  fieldPractices: Record<string, FieldPracticeResponse>,
  year: number,
  cropTypes: CROP_TYPE_FCPO[]
): boolean => fields.every((field) => cropTypes.some((cropType) => fieldHasCropType(fieldPractices, field.id, year, cropType)));

// subset of getFieldsWithoutCropType's exclusions, surfaced so the UI can explain why
export const getFieldsWithLockedYear = ({
  fields,
  fieldPractices,
  year,
  cropType,
}: {
  fields: FieldListField[];
  fieldPractices: Record<string, FieldPracticeResponse>;
  year: number;
  cropType: CROP_TYPE_FCPO;
}): FieldListField[] => fields.filter((field) => isCropTypeYearLocked(fieldPractices, field.id, year, cropType));

export const sumFieldHectares = (fields: FieldListField[]): number => fields.reduce((total, field) => total + (field.size ?? 0), 0);
